import { Component, OnInit, inject } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { CriteriaNavigateService } from '@clubhub/ignite/services';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { LoginMenuComponent } from '@shared/components/login-menu/login-menu.component';
import { roles } from '@shared/constants';
import { ClientApps } from '@shared/models/enums';
import { SlugifyPipe } from '@shared/pipes/slugify.pipe';
import { AuthorizeService } from '@shared/services/authorize.service';

interface NavItem {
  label: string;
  visible?: () => boolean;
  routerLink?: string;
  onClick?: () => void;
  subItems?: NavItem[];
}

@Component({
  selector: 'app-header',
  templateUrl: 'header.component.html',
  styleUrls: ['header.component.scss'],
  imports: [
    RouterLink,
    NgbDropdown,
    RouterLinkActive,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
    LoginMenuComponent,
    SlugifyPipe,
  ],
})
export class HeaderComponent implements OnInit {
  private readonly authService = inject(AuthorizeService);
  private readonly criteriaNavigateService = inject(CriteriaNavigateService);

  public nav: NavItem[] = [];
  public clientApp: ClientApps = ClientApps.UserManager;

  public ngOnInit() {
    this.nav = [
      {
        label: 'Home',
        visible: () => this.hasRole(roles.adminDashboard),
        routerLink: '/',
      },
      {
        label: 'Diagnostics',
        visible: () => this.hasRole(roles.adminDashboard) || this.hasRole(roles.screenscrapeTesting),
        routerLink: '/diagnostics',
        subItems: [
          {
            label: 'Web jobs',
            visible: () => this.hasRole(roles.adminDashboard),
            routerLink: '/diagnostics/webjobs',
          },
          {
            label: 'Screenscrape tests',
            visible: () => this.hasRole(roles.screenscrapeTesting),
            routerLink: '/diagnostics/screenscrapetests',
          },
          {
            label: 'Playwright UI test reports',
            visible: () => this.hasRole(roles.adminDashboard),
            routerLink: '/diagnostics/playwright-ui-test-reports',
          },
        ],
      },
      {
        label: 'Accounts',
        visible: () => this.isUserManagementAny || this.hasRole(roles.externalClientManagement),
        onClick: () => null,
        routerLink: '/accounts',
        subItems: [
          {
            label: 'User management',
            visible: () => this.isUserManagementAny,
            routerLink: '/accounts/users',
          },
          {
            label: 'Add internal user',
            visible: () => this.hasRole(roles.adminDashboardUserManagement),
            routerLink: '/accounts/adduser',
          },
          {
            label: 'External client management',
            visible: () => this.hasRole(roles.externalClientManagement),
            routerLink: '/accounts/externalclients',
          },
          {
            label: 'User management audit log',
            visible: () => this.hasRole(roles.adminDashboardUserManagement),
            routerLink: '/accounts/useraudit',
          },
          {
            label: 'Temporary token audit log',
            visible: () => this.hasRole(roles.externalClientManagement),
            routerLink: '/accounts/temporarytokenaudit',
          },
        ],
      },
      {
        label: 'Form management',
        visible: () => this.hasRole(roles.formManagement),
        subItems: [
          {
            label: 'Criteria management',
            visible: () => this.hasRole(roles.formManagement),
            routerLink: 'formmanagement/criteriamanagement/dashboard',
          },
          {
            label: 'Recent updates',
            visible: () => this.hasRole(roles.formManagement),
            routerLink: 'formmanagement/recentupdates',
          },
        ],
      },
      {
        label: 'Lender management',
        visible: () => this.hasRole(roles.lenderManagement),
        subItems: [
          {
            label: 'Lender management (new)',
            visible: () => this.hasRole(roles.lenderManagement),
            routerLink: '/ignite/criteriaV2/landing/lenderManagement',
          },
          {
            label: 'Lender groups',
            visible: () => this.hasRole(roles.lenderManagement),
            routerLink: '/ignite/criteriaV2/landing/lenderGroups',
          },
          {
            label: 'All lender management',
            visible: () => this.hasRole(roles.lenderManagement),
            routerLink: '/ignite/criteriaV2/landing/allLenderManagement',
          },
          {
            label: 'Configure onboarded lenders',
            visible: () => this.hasRole(roles.lenderManagement),
            routerLink: '/ignite/criteriaV2/landing/1/lenders',
          },
          {
            label: 'Lender management audit log',
            visible: () => this.hasRole(roles.lenderManagement),
            routerLink: '/ignite/criteriaV2/landing/lenderManagementAuditLog',
          },
        ],
      },
      {
        label: 'Distribution groups',
        visible: () => this.hasRole(roles.distributionGroupManagement) || this.hasRole(roles.siteManagement),
        routerLink: '/groupmanagement',
        subItems: [
          {
            label: 'Distribution group',
            routerLink: '/groupmanagement/distributionGroup',
            visible: () => this.hasRole(roles.distributionGroupManagement),
          },
          {
            label: 'Exclusives',
            routerLink: '/groupmanagement/exclusives',
            visible: () => this.hasRole(roles.siteManagement),
          },
          {
            label: 'Exclusives audit log',
            routerLink: '/groupmanagement/exclusivesAuditLog',
            visible: () => this.hasRole(roles.siteManagement),
          },
        ],
      },
      {
        label: 'Proc fee management',
        visible: () => this.hasRole(roles.procFeeManagement),
        subItems: [
          {
            label: 'Proc fee management',
            routerLink: '/procfeemanagement',
          },
          {
            label: 'Proc fee bulk templates',
            routerLink: '/procfeemanagement/bulkprocfeestemplates',
          },
        ],
      },
      {
        label: 'Broker licence management',
        visible: () => this.hasRole(roles.brokerLicenceAdmin),
        routerLink: '/BrokerLicenceAdmin',
      },
      {
        label: 'Analytics',
        visible: () => this.hasRole(roles.analytics),
        subItems: [
          {
            label: 'Overview',
            routerLink: '/analytics/overview',
          },
          {
            label: 'Affordability search audit',
            routerLink: '/analytics/affordability-search-audit',
          },
          {
            label: 'Service limits',
            routerLink: '/analytics/service-limits',
          },
        ],
      },
      {
        label: 'Site management',
        visible: () => this.hasRole(roles.siteManagement),
        routerLink: '/site_management',
        subItems: [
          {
            label: 'ONS data',
            routerLink: '/site_management/onsdataupload',
          },
          {
            label: 'UI component catalogue',
            routerLink: '/ui-component-catalogue',
          },
          {
            label: 'Upload FCA Firm data',
            routerLink: '/site_management/upload-fca-firm-data',
          },
          {
            label: 'Firm directory',
            routerLink: '/site_management/firm-directory',
          },
          {
            label: 'Email templates',
            routerLink: '/site_management/email-templates',
          },
        ],
      },
    ];
  }

  public get isLoggedIn(): boolean {
    return this.authService.isLoggedIn;
  }

  public get isUserManagementAny() {
    return [
      roles.clubHubUserManagementLimited,
      roles.clubHubUserManagement,
      roles.lenderHubUserManagement,
      roles.adminDashboardUserManagement,
    ].some(r => this.hasRole(r));
  }

  public get logoAltText() {
    return this.authService.isIgniteApp ? 'Ignite home' : 'ClubHub home';
  }

  // Short form checking of logged in and checking auth service has role
  public hasRole(role: string) {
    return this.isLoggedIn && this.authService.hasRole(role);
  }

  public openIssues() {
    this.criteriaNavigateService.navigate('issues', { queryParams: { flowType: 'edit' } });
  }
}
